import { PlusOutlined } from '@ant-design/icons';
import { Button, Select, Spin, Stack } from 'shared';
import { fullWidthStyles } from 'App';
import React from 'react';
import { useState } from 'react';
import { SentryApiClient } from '_generated/api';
import { SensorLabel } from './SensorLabel';
import {
  isSiteSensorBatchArray,
  SiteSensorBatch,
} from 'Sensors/hooks/useComparableSensors';
import { Skeleton } from 'antd/lib';

export interface SensorSelectorProps {
  comparableSensors?: SentryApiClient.SensorDTO[] | SiteSensorBatch[];
  loading?: boolean;
  onSelect?: (sensor: SentryApiClient.SensorDTO) => void;
}

export const SensorSelector: React.FC<SensorSelectorProps> = ({
  comparableSensors,
  loading,
  onSelect,
}) => {
  const [isSelecting, setSelecting] = useState(false);

  const selectSensor = (id: number) => {
    const sensor = isSiteSensorBatchArray(comparableSensors)
      ? comparableSensors
          .flatMap((batch) => batch.sensors.items ?? [])
          .find((s) => s.id === id)
      : comparableSensors?.find((s) => s.id === id);

    if (sensor) {
      onSelect && onSelect(sensor);
      setSelecting(false);
    }
  };

  return (
    <>
      {!isSelecting && (
        <div>
          <Button icon={<PlusOutlined />} onClick={() => setSelecting(true)}>
            Add Sensor
          </Button>
        </div>
      )}
      {isSelecting && (
        <Stack alignment="middle" gap="xs">
          <div>Compare to:</div>
          <Stack.Item>
            {loading && <Skeleton.Input block active />}
            {!loading && (
              <Select<number>
                showSearch
                autoFocus
                aria-label="Choose a sensor to compare"
                className={fullWidthStyles.fullWidth}
                placeholder="Select a sensor..."
                onChange={(v) => selectSensor(v)}
                optionFilterProp={'label'}
              >
                {isSiteSensorBatchArray(comparableSensors) &&
                  comparableSensors
                    .filter((s) => (s.sensors.items?.length ?? 0) > 0)
                    .map((s) => (
                      <Select.OptGroup key={s.siteName} label={s.siteName}>
                        {s.sensors.items?.map((sensor) => (
                          <Select.Option
                            key={sensor.id}
                            value={sensor.id ?? -1}
                            label={sensor.sensorName ?? 'Sensor'}
                            aria-label={sensor.sensorName ?? 'Sensor'}
                          >
                            <SensorLabel sensor={sensor} />
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    ))}
                {!isSiteSensorBatchArray(comparableSensors) &&
                  comparableSensors?.map((s) => (
                    <Select.Option
                      key={s.id}
                      value={s.id ?? -1}
                      label={s.sensorName ?? 'Sensor'}
                      aria-label={s.sensorName ?? 'Sensor'}
                    >
                      <SensorLabel sensor={s} />
                    </Select.Option>
                  ))}
              </Select>
            )}
          </Stack.Item>
        </Stack>
      )}
    </>
  );
};
